import {NodeSubProduct, NodeSubProductContainer} from "@/components/templates/nodes/node--product--subproduct";
import dynamic from "next/dynamic";
import {
  Dictionary,
  DrupalDocumentMedia,
  DrupalProductNode,
  DrupalProductVariationGroup,
  DrupalSearchApiResponseMeta,
  WorkflowType,
  DrupalProductVariationType, DrupalProductVariationProductNumber,
} from "@/types/hygiena-types";
import {Fragment, useContext, useRef, useState} from "react";
import {DictionaryContext} from "@/context/dictionary-context";
import {Body} from "@/components/atoms/body";
import {Link} from "@/components/atoms/link";
import SectionHeading from "@/components/organisms/section-heading";
import {WidgetTableField} from "@/components/organisms/widget--table-field";
import {DrupalNode} from "next-drupal";
import {BenefitSection} from "@/components/templates/nodes/node--benefit";
import TldrPodcast from "@/components/shadcn/_custom/tldr-podcast";
import {absoluteURL} from "@/lib/absolute-url";
import {getParagraphSections} from "@/lib/get-paragraph-sections";
import {ModalContext} from "@/context/modal-context";
import classNames from "classnames";
import {getProductVariations} from "@/lib/get-product-variations";
import {MediaImage} from "@/components/templates/media/media--image";
import {PRODUCT_TEASER_IMAGE_STYLE, THUMBNAIL_IMAGE_STYLE} from "@/types/image-styles";
import {Button} from "@/components/atoms/button";
import {getVariationLabel} from "@/components/organisms/widget--variation-select";
const RenderSections = dynamic(() => import("../templates/sections/section-regions").then(mod => mod.RenderSections));
const FormattedText = dynamic(() => import("../formatted-text").then((mod) => mod.FormattedText));
const DynamicProductGrid = dynamic(() => import("../organisms/widget--product-grid").then(mod => mod.DynamicProductGrid));
const ProductGroupingTitle = dynamic(() => import("../molecules/product-grouping-title").then((mod) => mod.ProductGroupingTitle));
const ProductSectionDivider = dynamic(() => import("../molecules/product-section-divider").then((mod) => mod.ProductSectionDivider));
const CompatiblesProductGrid = dynamic(() => import("../molecules/compatibles-product-grid").then((mod) => mod.CompatiblesProductGrid));
const ProductSlider = dynamic(() => import("../organisms/slider").then((mod) => mod.ProductSlider));
const TrainingVideosSlider = dynamic(() => import("../organisms/slider").then((mod) => mod.TrainingVideosSlider));
const ArticleSlider = dynamic(() => import("../organisms/slider").then((mod) => mod.ArticleSlider));
const WidgetResourcesSection = dynamic(() => import("../organisms/widget--resources-section").then(mod => mod.WidgetResourcesSection));

interface ProductSectionProps {
  node: DrupalProductNode;
  sectionHeaderSize: 2 | 3;
  subProductHeaderSize: 3 | 4;
  isWorkflow?: boolean;
}

/**
 * A product section to display Equipment.
 *
 * @param node
 * @param sectionHeaderSize
 * @param subProductHeaderSize
 * @param isWorkflow
 * @constructor
 */
export function ProductSectionEquipment({
  node,
  sectionHeaderSize,
  subProductHeaderSize,
  isWorkflow = false,
}: ProductSectionProps) {
  const t = useContext<Dictionary>(DictionaryContext);
  const {setOpenModal, setOpenModalContent} = useContext(ModalContext);

  if (!node?.field_product_equipment?.length) return null;
  return (
    <ProductSectionDivider data-workflow={WorkflowType.Equipment}>
      <ProductGroupingTitle node={node.field_product_equipment} defaultTitle={`${t?.all?.equipment}`} headerType={sectionHeaderSize} smallTitle={isWorkflow}/>
      {!isWorkflow ? (
        <NodeSubProductContainer>
          {node.field_product_equipment.map(item => <NodeSubProduct node={item} key={`equipment--${item.id}`} headerType={subProductHeaderSize} openModal={setOpenModal} openModalContent={setOpenModalContent}/>)}
        </NodeSubProductContainer>
      ): (
        <DynamicProductGrid nodes={node.field_product_equipment}/>
      )}
    </ProductSectionDivider>
  );
}

/**
 * A product section to display Instruments.
 *
 * @param node
 * @param sectionHeaderSize
 * @param subProductHeaderSize
 * @param isFoodproofWorkflow
 * @param isWorkflow
 * @constructor
 */
export function ProductSectionInstruments({
  node,
  sectionHeaderSize,
  subProductHeaderSize,
  isFoodproofWorkflow,
  isWorkflow = false,
}: {
  isFoodproofWorkflow: boolean;
} & ProductSectionProps) {
  const t = useContext<Dictionary>(DictionaryContext);
  const {setOpenModal, setOpenModalContent}  = useContext(ModalContext);

  if (!node?.field_product_instruments?.length) return null;
  return (
    <ProductSectionDivider data-workflow={WorkflowType.Instruments}>
      <ProductGroupingTitle node={node?.field_product_instruments}
                            marginBottom={isFoodproofWorkflow ? "1" : undefined}
                            smallTitle={isWorkflow}
                            defaultTitle={`${t?.all?.real_time_pcr_kits}`} headerType={sectionHeaderSize}>
        <>
          {isFoodproofWorkflow && (
            <Body elementType="p" bodyType="lg" className="mb-4">
              {`${t?.all?.foodproof_instruments_header}`}
            </Body>
          )}
        </>
      </ProductGroupingTitle>
      {(!isWorkflow || node.field_product_instruments.length === 1) ? (
        <NodeSubProductContainer>
          {node.field_product_instruments.map(item => <NodeSubProduct node={item} key={`instruments--${item.id}`} headerType={subProductHeaderSize} openModal={setOpenModal} openModalContent={setOpenModalContent}/>)}
        </NodeSubProductContainer>
      ): (
          <DynamicProductGrid
            nodes={node.field_product_instruments}
            />
      )}
    </ProductSectionDivider>
  );
}

/**
 * A product section to display Additionals.
 *
 * @param node
 * @constructor
 */
export function ProductSectionAdditionals({node}: {node: DrupalProductNode}) {
  const t = useContext<Dictionary>(DictionaryContext);

  if (!node?.field_product_additionals) return null;
  return (
    <ProductSectionDivider data-workflow={WorkflowType.Additionals} className="!mb-8">
      <div className="text-left">
        <Body bodyType={"lg"} elementType={"span"} className="pr-2">
          {node?.field_product_additional_label} <Link href={node?.field_product_additionals?.path?.alias ?? "/"} className="text-primary">{node.field_product_additionals.title}</Link>.
        </Body>
      </div>
    </ProductSectionDivider>
  );
}

/**
 * A product section to display Automation.
 *
 * @param node
 * @param sectionHeaderSize
 * @param subProductHeaderSize
 * @param isWorkflow
 * @constructor
 */
export function ProductSectionAutomation({
  node,
  sectionHeaderSize,
  subProductHeaderSize,
  isWorkflow = false,
}: ProductSectionProps) {
  const t = useContext<Dictionary>(DictionaryContext);
  const {setOpenModal, setOpenModalContent} = useContext(ModalContext);

  if (!node?.field_product_automation?.length) return null;
  return (
    <ProductSectionDivider data-workflow={WorkflowType.Automation}>
      <ProductGroupingTitle node={node.field_product_automation} defaultTitle={`${t?.all?.automation}`} headerType={sectionHeaderSize} smallTitle={isWorkflow}/>
      {(!isWorkflow || node.field_product_automation.length === 1) ? (
        <NodeSubProductContainer>
          {node.field_product_automation.map(item => <NodeSubProduct node={item} key={`automation--${item.id}`} headerType={subProductHeaderSize} openModal={setOpenModal} openModalContent={setOpenModalContent}/>)}
        </NodeSubProductContainer>
      ): (
        <DynamicProductGrid nodes={node.field_product_automation} />
      )}
    </ProductSectionDivider>
  );
}

/**
 * A product section to display Monitoring.
 *
 * @param node
 * @param sectionHeaderSize
 * @param subProductHeaderSize
 * @param isWorkflow
 * @constructor
 */
export function ProductSectionMonitoring({
  node,
  sectionHeaderSize,
  subProductHeaderSize,
  isWorkflow = false,
}: ProductSectionProps) {
  const t = useContext<Dictionary>(DictionaryContext);
  const {setOpenModal, setOpenModalContent} = useContext(ModalContext);

  if (!node?.field_product_monitoring_systems?.length) return null;
  return (
    <ProductSectionDivider data-workflow={WorkflowType.Monitoring}>
      <ProductGroupingTitle node={node?.field_product_monitoring_systems} defaultTitle={`${t?.all?.monitoring_systems}`} headerType={sectionHeaderSize} smallTitle={isWorkflow}/>
      {(!isWorkflow || node.field_product_monitoring_systems.length === 1) ? (
        <NodeSubProductContainer>
          {node.field_product_monitoring_systems.map(item => <NodeSubProduct node={item} key={`monitoring-systems--${item.id}`} headerType={subProductHeaderSize} openModal={setOpenModal} openModalContent={setOpenModalContent}/>)}
        </NodeSubProductContainer>
      ): (
        <DynamicProductGrid nodes={node.field_product_monitoring_systems} />
      )}
    </ProductSectionDivider>
  );
}

/**
 * A product section to display Sample Collection.
 *
 * @param node
 * @param sectionHeaderSize
 * @param subProductHeaderSize
 * @param isWorkflow
 * @constructor
 */
export function ProductSectionSampleCollection ({
  node,
  sectionHeaderSize,
  isWorkflow = false,
}: {
  node: DrupalProductNode;
  sectionHeaderSize: 2 | 3;
  isWorkflow?: boolean;
}) {
  const t = useContext<Dictionary>(DictionaryContext);

  if (!node?.field_product_sample_collection?.length) return null;
  return (
    <ProductSectionDivider data-workflow={WorkflowType.SampleCollection}>
      <ProductGroupingTitle node={node.field_product_sample_collection} defaultTitle={`${t?.all?.sample_collection}`} headerType={sectionHeaderSize} smallTitle={isWorkflow}/>
      <DynamicProductGrid nodes={node.field_product_sample_collection} />
    </ProductSectionDivider>
  );
}

/**
 * A product section to display Software.
 *
 * @param node
 * @param sectionHeaderSize
 * @param subProductHeaderSize
 * @param isWorkflow
 * @constructor
 */
export function ProductSectionSoftware ({
  node,
  sectionHeaderSize,
  subProductHeaderSize,
  isWorkflow = false,
}: ProductSectionProps) {
  const t = useContext<Dictionary>(DictionaryContext);
  const {setOpenModal, setOpenModalContent} = useContext(ModalContext);

  if (!node?.field_product_software?.length) return null;
  return (
    <ProductSectionDivider data-workflow={WorkflowType.Software}>
      <ProductGroupingTitle node={node?.field_product_software} defaultTitle={`${t?.all?.data_analysis}`} headerType={sectionHeaderSize} smallTitle={isWorkflow}/>
      {(!isWorkflow || node.field_product_software.length === 1) ? (
        <NodeSubProductContainer>
          {node.field_product_software.map(item => <NodeSubProduct node={item} key={`data-analysis--${item.id}`} headerType={subProductHeaderSize} openModal={setOpenModal} openModalContent={setOpenModalContent}/>)}
        </NodeSubProductContainer>
      ): (
        <DynamicProductGrid nodes={node.field_product_software} />
      )}
    </ProductSectionDivider>
  );
}

/**
 * A product section to display Real Additionals.
 *
 * @param node
 * @param sectionHeaderSize
 * @param subProductHeaderSize
 * @param isWorkflow
 * @constructor
 */
export function ProductSectionRealAdditionals ({
  node,
  sectionHeaderSize,
  subProductHeaderSize,
  isWorkflow = false,
}: ProductSectionProps) {
  const t = useContext<Dictionary>(DictionaryContext);
  const {setOpenModal, setOpenModalContent} = useContext(ModalContext);

  if (!node?.field_product_real_additionals?.length) return null;
  return (
    <ProductSectionDivider>
      <ProductGroupingTitle node={undefined} defaultTitle={`${t?.all?.additionals}`} headerType={sectionHeaderSize} smallTitle={isWorkflow}/>
      {(!isWorkflow || node.field_product_real_additionals.length === 1) ? (
        <NodeSubProductContainer>
          {node.field_product_real_additionals.map(item => <NodeSubProduct node={item} key={`additionals--${item.id}`} headerType={subProductHeaderSize} openModal={setOpenModal} openModalContent={setOpenModalContent}/>)}
        </NodeSubProductContainer>
      ): (
        <DynamicProductGrid nodes={node.field_product_real_additionals} />
      )}
    </ProductSectionDivider>
  );
}

/**
 * A product section to display Media.
 *
 * @param node
 * @param sectionHeaderSize
 * @param subProductHeaderSize
 * @param isWorkflow
 * @constructor
 */
export function ProductSectionMedia ({
  node,
  sectionHeaderSize,
  subProductHeaderSize,
  isWorkflow = false,
}: ProductSectionProps) {
  const t = useContext<Dictionary>(DictionaryContext);
  const {setOpenModal, setOpenModalContent} = useContext(ModalContext);

  if (!(node?.field_product_enrichment?.length || node?.field_product_enrichment?.id)) return null;
  return (
    <ProductSectionDivider data-workflow={WorkflowType.Media}>
      <ProductGroupingTitle node={undefined} defaultTitle={`${t?.all?.enrichment}`} marginBottom={"1"} headerType={sectionHeaderSize} smallTitle={isWorkflow}>
        <Body bodyType={"lg"} className="mb-4">
          {`${t?.all?.enrichment_media_header}`}
        </Body>
      </ProductGroupingTitle>
      {!isWorkflow ? (
        <NodeSubProductContainer>
          {Array.isArray(node?.field_product_enrichment) && node?.field_product_enrichment.length ? (
            <>
              {node.field_product_enrichment.map(item => (
                <NodeSubProduct key={`enrichment--${item?.id}`} node={item} headerType={subProductHeaderSize} openModal={setOpenModal} openModalContent={setOpenModalContent}/>
              ))}
            </>
          ): <></>}
          {!Array.isArray(node?.field_product_enrichment) && node?.field_product_enrichment ? (
            <NodeSubProduct node={node.field_product_enrichment} headerType={subProductHeaderSize} openModal={setOpenModal} openModalContent={setOpenModalContent}/>
          ): <></>}
        </NodeSubProductContainer>
      ): (
        <>
          {(Array.isArray(node?.field_product_enrichment) && node?.field_product_enrichment.length > 1) ? (
            <DynamicProductGrid nodes={node.field_product_enrichment} />
          ): (
            <NodeSubProductContainer>
              {node.field_product_enrichment.map(item => (
                <NodeSubProduct key={`enrichment--${item?.id}`} node={item} headerType={subProductHeaderSize} openModal={setOpenModal} openModalContent={setOpenModalContent}/>
              ))}
            </NodeSubProductContainer>
          )}
        </>
      )}
    </ProductSectionDivider>
  );
}

/**
 * A product section to display Compatibles.
 *
 * @param node
 * @param additionalContent
 * @constructor
 */
export function ProductSectionCompatibles ({
  node,
  additionalContent,
}: {
  node: DrupalProductNode;
  additionalContent?: {
    compatibleProducts?: DrupalProductNode[],
    compatibleMeta?: DrupalSearchApiResponseMeta,
  },
}) {
  const t = useContext<Dictionary>(DictionaryContext);

  if (!additionalContent?.compatibleProducts?.length) return null;

  const getCompatiblesTitle = () => {
    const parts: string[] = [];
    if (node?.field_comp_heading?.processed) {
      return node?.field_comp_heading;
    }

    parts.push(`${t?.all?.products_compatible_with}`);
    if (node?.field_product_brand?.name) {
      if (node?.field_product_brand?.field_brand_suffix) {
        parts.push(`${node.field_product_brand.name}${node.field_product_brand.field_brand_suffix}`);
      }
      else {
        parts.push(node.field_product_brand.name);
      }
    }
    if (node?.field_product_display_title?.value) parts.push(node?.field_product_display_title?.value);
    else parts.push(node?.title);
    return {processed: parts.join(" ")};
  }

  const compatiblesTitle = getCompatiblesTitle();
  const compatiblesDesc = node?.field_comp_description;

  return (
    <ProductSectionDivider data-workflow={node?.field_comp_workflow_type ?? undefined}>
      <CompatiblesProductGrid id={node.id} gridKey={node?.id} title={compatiblesTitle} description={compatiblesDesc} products={additionalContent?.compatibleProducts ?? []} meta={additionalContent?.compatibleMeta}/>
    </ProductSectionDivider>
  );
}

/**
 * A product section to display Product Accessories.
 *
 * @param node
 * @constructor
 */
export function ProductSectionAccessories ({
  node,
}: {
  node: DrupalProductNode;
}) {
  const t = useContext<Dictionary>(DictionaryContext);

  /**
   * Gets the accessories rows as an array.
   * @param accessories
   */
  const getAccessoriesRows = (accessories): {[key:string]: string}[] => {
    let rows: {[key:string]: string}[] = [];
    if (!Array.isArray(accessories) && typeof accessories === "object") {
      rows = Object.keys(accessories).map((i, key) => {
        let hasVal = false;
        // Ignore empty rows.
        if (!isNaN(key)) {
          const item = accessories?.[key];
          if (item) {
            for (const i of Object.keys(item)) {
              if (i !== "weight" && item[i]) {
                hasVal = true;
                break;
              }
            }
          }
        }
        if (hasVal) return accessories[key];
      });
      rows = rows.filter(item => typeof item !== "undefined");
    }
    return rows;
  }

  /**
   * Returns a formated accessories header.
   */
  const getAccessoriesHeader = () => {
    if (node?.field_product_accessories_header?.processed?.length) return node.field_product_accessories_header.processed
    const components: string[] = [`${t?.all?.accessories_for}`];
    if (node?.field_product_brand?.name) components.push(node.field_product_brand.name);
    if (node?.field_product_brand?.field_brand_suffix) components.push(node.field_product_brand.field_brand_suffix);
    components.push(node.title);
    return components.join(" ");
  }

  const accessoriesRows = getAccessoriesRows(node?.field_product_accessories_table?.value);
  const accessoriesHeader = getAccessoriesHeader();
  const accessoriesCaption = node?.field_product_accessories_table?.caption ?? "";

  if (accessoriesRows?.length <= 1) return null;
  return (
    <ProductSectionDivider>
      {accessoriesHeader && (
        <SectionHeading displayTitle={accessoriesHeader} title={""}>
          {accessoriesCaption && <FormattedText processed={accessoriesCaption}/>}
        </SectionHeading>
      )}
      <WidgetTableField rows={accessoriesRows} keyPrefix={node?.id} key={`widget-table-field--${node?.id}`}/>
    </ProductSectionDivider>
  );
}

/**
 * A product section to display Product Training Videos.
 *
 * @param node
 * @constructor
 */
export function ProductSectionTrainingVideos ({
  node,
}: {
  node: DrupalProductNode;
}) {
  const t = useContext<Dictionary>(DictionaryContext);
  const {setOpenModal, setOpenModalContent} = useContext(ModalContext);
  if (!node?.field_product_training_videos?.length) return null;
  return (
    <ProductSectionDivider>
      <div className="container pt-4 mx-auto">
        <div className={"mb-6"}>
          <SectionHeading displayTitle={node?.field_product_training_title ?? t?.video_library?.product_training_videos}>
            {node?.field_product_training_vid_desc?.processed?.length && (
              <FormattedText processed={node.field_product_training_vid_desc.processed}/>
            )}
          </SectionHeading>
        </div>
        <TrainingVideosSlider videos={node.field_product_training_videos ?? []} openModalContent={setOpenModalContent} openModal={setOpenModal}/>
        <div className="text-right pr-2 text-primary">
          <Link href={"/video-library"}>{t?.video_library?.show_all_videos}</Link>
        </div>
      </div>
    </ProductSectionDivider>
  );
}

/**
 * A product section to display Related Products.
 *
 * @param node
 * @constructor
 */
export function ProductSectionRelatedProducts ({
  node,
}: {
  node: DrupalProductNode;
}) {
  const t = useContext<Dictionary>(DictionaryContext);

  if (!node?.field_product_products?.length) return null;
  return (
    <ProductSectionDivider>
      <div className="container pt-4 mx-auto">
        <div className={"mb-6"}>
          <h2 className="mb-2 text-gray-800 inline">
            {`${t?.all?.related_products}`}
          </h2>
        </div>
        <ProductSlider products={node.field_product_products}/>
      </div>
    </ProductSectionDivider>
  );
}

/**
 * A product section to display Related News.
 *
 * @param relatedNews
 * @constructor
 */
export function ProductSectionRelatedNews ({
  relatedNews,
}: {
  relatedNews?: DrupalNode[]
}) {
  const t = useContext<Dictionary>(DictionaryContext);

  if (!relatedNews?.length) return null;
  return (
    <ProductSectionDivider>
      <div className="container md:px-6 mx-auto">
        <div className={"mb-6"}>
          <h2 className="mb-2 text-gray-800 inline">
            {`${t?.all?.related_news}`}
          </h2>
          <Link href={"/news"} className="text-primary inline pl-6 max-md:hidden">{`${t?.all?.see_all_news}`}<span>&#8594;</span></Link>
        </div>
        <ArticleSlider articles={relatedNews}/>
      </div>
    </ProductSectionDivider>
  );
}

/**
 * A product section to display Extractions.
 *
 * @param node
 * @param sectionHeaderSize
 * @param subProductHeaderSize
 * @param isWorkflow
 * @constructor
 */
export function ProductSectionExtractions({
  node,
  sectionHeaderSize,
  subProductHeaderSize,
  isWorkflow = false,
}: {
  node: DrupalProductNode;
  sectionHeaderSize: 2 | 3;
  subProductHeaderSize: 3 | 4;
  isWorkflow?: boolean;
}) {
  const t = useContext<Dictionary>(DictionaryContext);
  const {setOpenModal, setOpenSmallModal, setOpenModalContent, setOpenSmallModalContent} = useContext(ModalContext);
  let extractionKitCount = 0;

  /**
   * Gets the initial extraction field.
   * @param fieldname
   */
  const getInitialExtraction = (fieldname: string) => {
    if (Array.isArray(node?.[fieldname])) {
      if (node?.[fieldname]?.length) return node[fieldname];
      return null;
    }
    return node?.[fieldname];
  }


  /**
   * Helper for returning the default reagents on page load.
   *
   * @return DrupalProductNode|null
   */
  const getDefaultReagents = () => {
    if (extractionStandard) return reagentsStandard;
    else if (extractionAutomated) return reagentsAutomated;
    else if (extractionHighThroughput) return reagentsHighThroughput;
    else if (extractionHighQuality) return reagentsHighQuality;
    return null;
  }


  /**
   * Helper for returning the default equipment on page load.
   *
   * @return DrupalProductNode|null
   */
  const getDefaultEquipment = () => {
    if (extractionStandard) return equipmentStandard;
    else if (extractionAutomated) return equipmentAutomated;
    else if (extractionHighThroughput) return equipmentHighThroughput;
    else if (extractionHighQuality) return extractionHighQuality;
    return null;
  }

  /**
   * Helper for returning the default automation on page load.
   *
   * @return DrupalProductNode|null
   */
  const getDefaultAutomation = () => {
    if (extractionAutomated) return node?.field_product_aut_automated;
    return null;
  }

  /**
   * Updates the extraction, reagents and equipment.
   *
   * @param type
   *   The extraction type.
   */
  const updateExtraction = (type: string) => {
    switch (type) {

      case "standard":
        setReagents(reagentsStandard);
        setEquipment(equipmentStandard);
        setExtraction(extractionStandard);
        setExtAutomation(null);
        break;

      case "high_quality":
        setReagents(reagentsHighQuality);
        setEquipment(equipmentHighQuality);
        setExtraction(extractionHighQuality);
        setExtAutomation(null);
        break;

      case "high_throughput":
        setReagents(reagentsHighThroughput);
        setEquipment(equipmentHighThroughput);
        setExtraction(extractionHighThroughput);
        setExtAutomation(null);
        break;

      case "automated":
        setReagents(reagentsAutomated);
        setEquipment(equipmentAutomated);
        setExtraction(extractionAutomated);
        setExtAutomation(node?.field_product_aut_automated);
        break;

      default:
        setReagents(null);
        setEquipment(null);
        setExtraction(null);
        setExtAutomation(null);
        break;
    }
  }


  const extractionStandard = getInitialExtraction("field_product_ext_standard");
  const extractionAutomated = getInitialExtraction("field_product_ext_automated");
  const extractionHighQuality = getInitialExtraction("field_product_ext_high_quality");
  const extractionHighThroughput = getInitialExtraction("field_product_ext_throughput_h");

  const reagentsStandard = node.field_product_reg_standard;
  const reagentsAutomated = node.field_product_reg_automated;
  const reagentsHighQuality = node.field_product_reg_high_quality;
  const reagentsHighThroughput = node.field_product_reg_h_throughput;

  const equipmentStandard = node.field_product_eq_standard;
  const equipmentAutomated = node.field_product_eq_automated;
  const equipmentHighQuality = node.field_product_eq_high_quality;
  const equipmentHighThroughput = node.field_product_eq_h_throughput;

  // Get the total number of extraction kits available.
  for (const extraction of [extractionStandard, extractionAutomated, extractionHighThroughput, extractionHighQuality]) {
    if (extraction?.length || extraction?.id) extractionKitCount++;
  }

  const defaultExtraction = extractionKitCount === 1 && (extractionStandard || extractionAutomated || extractionHighThroughput || extractionHighQuality);

  // Only set a default extraction if there is one available otherwise the user will have to select.
  const [extraction, setExtraction] = useState(defaultExtraction);
  const [reagents, setReagents] = useState<DrupalProductNode|DrupalProductNode[]|null>(getDefaultReagents());
  const [equipment, setEquipment] = useState<DrupalProductNode|null>(getDefaultEquipment());
  const [extAutomation, setExtAutomation] = useState<DrupalProductNode|null>(getDefaultAutomation());

  if (!extractionKitCount) return null;
  return (
    <ProductSectionDivider data-workflow={WorkflowType.Extractions} >
      <ProductGroupingTitle node={undefined} defaultTitle={`${t?.all?.extraction}`} marginBottom={"1"} className={"scroll-m-20"} headerType={sectionHeaderSize} smallTitle={isWorkflow}/>
      <div className="flex justify-between items-baseline flex-wrap mb-2">
        <Body className="!font-light text-black md:text-right max-md:mb-6 max-md:w-full" bodyType="lg">
          <p>{`${t?.all?.extraction_kits_header}`}:</p>
        </Body>
        <div className="max-md:mb-6 md:text-right text-black max-md:w-full">
          <span className="text-xl text-right md:text-left block md:inline mb-2 md:mb-0">{`${t?.all?.select_extraction}`}:</span>
          <select className="border-black border mb-2 lg:mb-0 lg:ml-1 p-1 rounded-sm w-full lg:w-auto block lg:inline md:mx-2" defaultValue={"select"}
                  onChange={e => updateExtraction(e.target.value)}>
            {extractionKitCount > 1 && (
              <option value={"select"}>{`${t?.all?.select}`}</option>
            )}
            <option disabled={!extractionStandard} value="standard">{`${t?.all?.standard}`}</option>
            <option disabled={!extractionHighThroughput} value="high_throughput">{`${t?.all?.high_throughput}`}</option>
            <option disabled={!extractionHighQuality} value="high_quality">{`${t?.all?.high_quality}`}</option>
            <option disabled={!extractionAutomated} value="automated">{`${t?.all?.automated}`}</option>
          </select>
          <span
            onClick={() => {
              if (typeof setOpenSmallModal === 'function') {
                setOpenSmallModal(true);
              }
              if (setOpenSmallModalContent) {
                setOpenSmallModalContent(
                  <div className="text-black">
                    {/*@todo placeholder, localize this. */}
                    <div className="mb-8">
                      <h2 className="font-light pb-1">{`${t?.all?.standard}`}:</h2>
                      <div className="font-bold text-xl">foodproof® StarPrep Kits</div>
                      <div>Designed for the quick and easy manual extraction of DNA for multiple sample types.</div>
                    </div>
                    <div className="mb-8">
                      <h2 className="font-light pb-1">{`${t?.all?.high_throughput}`}:</h2>
                      <div className="font-bold text-xl">foodproof® StarPrep 8-Strip Kits</div>
                      <div>Designed for the rapid manual preparation of DNA from multiple samples in parallel.</div>
                    </div>
                    <div className="mb-8">
                      <h2 className="font-light pb-1">{`${t?.all?.high_quality}`}:</h2>
                      <div className="font-bold text-xl">foodproof® Sample Preparation Kits</div>
                      <div>Designed for the high-purity extraction of challenging samples.</div>
                    </div>
                    <div className="mb-8">
                      <h2 className="font-light pb-1">{`${t?.all?.fully_automated}`}:</h2>
                      <div className="font-bold text-xl">foodproof® Magnetic Preparation Kits</div>
                      <div>Designed for the high-purity extraction using the automated foodproof® RoboPrep® Series workstations.</div>
                    </div>
                  </div>
                );
              }
            }}
            className="border border-primary text-primary py-1 px-2.5 font-bold cursor-pointer rounded-sm text-lg max-md:hidden">?</span>
        </div>
      </div>
      {extraction ? (
        <>
            {Array.isArray(extraction) ? (
              <div className="mb-8">
                {extraction?.length === 1 ? extraction.map(item => (
                  <Fragment key={`extraction--${item.id}`}>
                    <NodeSubProductContainer>
                      {item?.id && (
                        <NodeSubProduct  node={item} openModal={setOpenModal} openModalContent={setOpenModalContent}/>
                      )}
                    </NodeSubProductContainer>
                  </Fragment>
                )): (
                  <DynamicProductGrid nodes={extraction} />
                )}
              </div>
            ): (
              <div className="mb-8">
                <NodeSubProductContainer>
                  <NodeSubProduct key={`extraction--${extraction.id}`} node={extraction} openModal={setOpenModal} openModalContent={setOpenModalContent}/>
                </NodeSubProductContainer>
              </div>
            )}
            {/* Display automated extraction automation */}
            {Array.isArray(extAutomation) && extAutomation.length ? (
              <div className={classNames("mb-8", {"md:pl-24": isWorkflow})}>
                <h4 className="md:!text-2xl text-black">
                  {`${t?.all?.automation}`}
                </h4>
                {extraction.length === 1 ? extAutomation.map(item => (
                  <NodeSubProductContainer key={`extraction-automated--${item.id}`}>
                    <NodeSubProduct node={item} headerType={subProductHeaderSize} openModal={setOpenModal} openModalContent={setOpenModalContent}/>
                  </NodeSubProductContainer>
                )): (
                  <DynamicProductGrid nodes={extAutomation} />
                )}
              </div>
            ): <></>}
            {/* Automation should always be an array, but check for an object just in case. */}
            {(typeof extAutomation === 'object' && extAutomation?.id) ? (
              <div className={classNames("mb-8", {"md:pl-24": isWorkflow})}>
                <NodeSubProductContainer>
                  <h4 className="md:!text-3xl font-light text-black">
                    {`${t?.all?.automation}`}
                  </h4>
                  <NodeSubProduct node={extAutomation} key={`extraction-automated--${extAutomation.id}`} headerType={subProductHeaderSize} openModal={setOpenModal} openModalContent={setOpenModalContent}/>
                </NodeSubProductContainer>
              </div>
            ):<></>}
            {(Array.isArray(reagents) && reagents?.length) ? (
              <div className={classNames("mb-8", {"md:pl-24": isWorkflow})}>
                <h4 className="md:!text-2xl text-black">
                  {`${t?.all?.additional_reagents}`}
                </h4>
                <div className="text-xl pb-4">{`${t?.all?.reagents_prefix}`}:</div>
                <div className="flex flex-col gap-12">
                  {reagents?.length > 1 ? (
                    <DynamicProductGrid nodes={reagents} />
                  ): (
                    <>
                      {reagents.map(item => (
                        <NodeSubProductContainer key={item.id}>
                          <NodeSubProduct node={item} headerType={subProductHeaderSize} openModal={setOpenModal} openModalContent={setOpenModalContent}/>
                        </NodeSubProductContainer>
                      ))}
                    </>
                  )}
                </div>
              </div>
            ): <></>}
            {!Array.isArray(reagents) && reagents?.id ? (
              <div className={classNames("mb-8", {"md:pl-24": isWorkflow})}>
                <h4 className="md:!text-2xl text-black pb-8">
                  {`${t?.all?.additional_reagents}`}
                </h4>
                <div className="text-xl pb-4">{`${t?.all?.reagents_prefix}`}:</div>
                <NodeSubProductContainer>
                  <NodeSubProduct node={reagents} key={reagents.id} headerType={subProductHeaderSize} openModal={setOpenModal} openModalContent={setOpenModalContent}/>
                </NodeSubProductContainer>
              </div>
            ): <></>}
            {equipment && (
              <div className={classNames("mb-8 scroll-m-20", {"md:pl-24": isWorkflow})} data-workflow={WorkflowType.Equipment}>
                <h4 className="md:!text-3xl font-light text-black pb-8">
                  {`${t?.all?.additional_equipment}`}
                </h4>
                <NodeSubProductContainer>
                  <NodeSubProduct node={equipment} key={equipment.id} headerType={subProductHeaderSize} openModal={setOpenModal} openModalContent={setOpenModalContent}/>
                </NodeSubProductContainer>
              </div>
            )}
        </>
      ): (
        <div className="bg-white drop-shadow-sm py-20 text-2xl text-center text-black rounded-md px-6 md:px-0">
          <div>{`${t?.all?.select_extraction_kit}`}</div>
          <div>({`${t?.all?.select_extraction_kit_see_menu}`})</div>
        </div>
      )}
    </ProductSectionDivider>
  );
}

/**
 * A product section to display Product Resources.
 *
 * @param relatedNews
 * @constructor
 */
export function ProductSectionResources({
  node,
}: {
  node: DrupalProductNode
}) {
  const t = useContext<Dictionary>(DictionaryContext);

  /**
   * Gets the available documents for the product.
   */
  const getProductDocuments = (node: DrupalProductNode) => {
    let documents: DrupalDocumentMedia[] = [];
    const productManual = node?.field_product_product_manual;
    const productBrochure = node?.field_product_brochure;
    const productSheet = node?.field_product_product_sheet;
    const safetyDataSheet = node?.field_product_safety_data_sheet;
    const readyReferenceSheet = node?.field_product_quick_guide;
    const softwarePatches = node?.field_product_software_patches;
    const additionalDocs = node?.field_product_additional_docs;
    const certificates = node?.field_product_certificates;
    for (const doc of [productManual, productBrochure, productSheet, safetyDataSheet, readyReferenceSheet, softwarePatches, additionalDocs, certificates]) {
      if (!doc || doc?.type === "unknown") continue;
      if (Array.isArray(doc) && doc.length) {
        documents = documents.concat(doc.filter(item => item?.type !== "unknown"));
      }
      else if (doc?.id && doc.id !== "unknown") {
        documents.push(doc);
      }
    }
    documents.sort((a, b) => a?.name?.localeCompare(b?.name));
    return documents;
  }

  const documents = getProductDocuments(node);

  if (!documents.length) return null;
  return (
    <ProductSectionDivider id="document-library">
      <SectionHeading title={`${t?.documents?.product_library}`}/>
      <WidgetResourcesSection documents={documents} />
    </ProductSectionDivider>
  );
}

/**
 * A product section to display Key Benefits.
 *
 * @param node
 * @constructor
 */
export function ProductSectionKeyBenefits ({
  node,
}: {
  node: DrupalProductNode
}) {
  const t = useContext<Dictionary>(DictionaryContext);
  const benefits = node.field_product_benefits.filter(item => item?.type !== "unknown" && item?.id !== "missing" && item?.field_benefit_default_benefit);

  if (!benefits?.length) return null;
  return (
    <ProductSectionDivider>
      <SectionHeading title={`${t?.all?.key_benefits}`}>
        {node?.field_product_benefits_summary?.processed && (
          <FormattedText processed={node.field_product_benefits_summary.processed}/>
        )}
      </SectionHeading>
      <BenefitSection benefits={node.field_product_benefits}/>
    </ProductSectionDivider>
  )
}

/**
 * A product section to display TLDR Podcasts.
 *
 * @param node
 * @constructor
 */
export function ProductSectionTLDRPodcasts({
  node,
}: {
  node: DrupalProductNode
}) {
  const t = useContext<Dictionary>(DictionaryContext);
  const podcastAudioUrl = node?.field_product_podcast?.field_media_file?.uri?.url;
  const podcastImage = node?.field_product_podcast?.field_podcast_image;

  if (!(node?.field_product_podcast && podcastImage && podcastAudioUrl)) return null;
  return (
    <ProductSectionDivider>
      <TldrPodcast
        title={node.field_product_podcast.field_podcast_title}
        description={node.field_product_podcast.field_podcast_description}
        category={node.field_product_podcast.field_podcast_category}
        coverImage={podcastImage} audioSource={`${absoluteURL(podcastAudioUrl)}`} />
    </ProductSectionDivider>
  )
}

/**
 * A product section to display Layout Components.
 *
 * @param node
 * @constructor
 */
export function ProductSectionLayoutComponents ({
  node,
}: {
  node: DrupalProductNode
}) {
  const t = useContext<Dictionary>(DictionaryContext);
  const {sections, sectionMap} = getParagraphSections(node?.field_product_layout);

  if (!sections.length) return null;
  return (
    <ProductSectionDivider container={false}>
      <RenderSections sections={sections} sectionMap={sectionMap}/>
    </ProductSectionDivider>
  )
}

/**
 * Renders the Buy Online product selector based upon product variations.
 *
 * @param node
 * @constructor
 */
export function ProductSectionBuyOnline ({
  node,
}: {
  node: DrupalProductNode
}) {
  const t = useContext<Dictionary>(DictionaryContext);
  const variations = getProductVariations(node);
  const variationsWithLinks = variations.filter(variation => variation.productNumbers.filter(item => item.ausStoreLink || item.usStoreLink).length > 0);

  if (!variationsWithLinks?.length) return <></>;

  /**
   * Helper to display a formatted product title.
   *
   * @return string
   */
  const formatTitle = (): string => {
    let title = '';
    if (node?.field_product_brand?.name) {
      title = `${node.field_product_brand.name}`;
      if (node?.field_product_brand?.field_brand_suffix) {
        title = `${title} ${node.field_product_brand.field_brand_suffix}`;
      }
    }
    if (node?.field_product_display_title?.processed) {
      title = `${title} ${node.field_product_display_title.processed}`;
    }
    else {
      title = `${title} ${node.title}`
    }
    return title;
  }

  /**
   * Displays the variation quantity with label translation.
   *
   * @param {DrupalProductVariationGroup} variation - The variation group.
   * @constructor
   */
  const DisplayVariationQuantity = ({variation}: {variation: DrupalProductVariationGroup}) => {
    switch (variation?.variation?.type) {
      case DrupalProductVariationType.Reactions:
        return <div>{`${t?.all?.reactions ?? ''} ${variation.variation.value}`}</div>
      case DrupalProductVariationType.Quantity:
        return <div>{`${t?.all?.quantity ?? ''} ${variation.variation.value}`}</div>
      case DrupalProductVariationType.Volume:
        return <div>{`${t?.all?.volume ?? ''} ${variation.variation.value}`}</div>
      case DrupalProductVariationType.Tests:
        return <div>{`${t?.all?.tests ?? ''} ${variation.variation.value}`}</div>
      case DrupalProductVariationType.Runs:
        return <div>{`${t?.all?.runs ?? ''} ${variation.variation.value}`}</div>
      default:
        return <></>;
    }
  }

  /**
   * Displays the order online button dropdown for the variant.
   *
   * @param {DrupalProductVariationProductNumber} productNumber - The productNumber object.
   * @constructor
   */
  const VariantButton = ({productNumber}: {productNumber: DrupalProductVariationProductNumber}) => {
    const ref = useRef(null);
    const [show, setShow] = useState(false);

    return (
      <div className={classNames("w-full relative text-center text-primary font-semibold")} onMouseLeave={() => setShow(false)} onClick={() => setShow(!show)}>
        <Button buttonRef={ref} callback={() => {}} icon={"chevron-down"} position={"right"} className={classNames(
          "h-full max-md:text-sm text-center py-1.5 px-1 w-full",
        )} containerClassName="w-full h-full" color={"primary"} hover={{color: "primary"}} responsiveIcon={true}>{t?.all?.order_product ?? 'Order Product'}</Button>
        <ul
          className={classNames(
            "absolute bg-white overflow-auto transition-all w-full z-10 scroll-primary",
            {"max-h-0": !show},
            {"max-h-[250px] transition-all": show}
          )} style={{width: ref?.current?.['offsetWidth'] ?? "auto"}}>
          {productNumber.usStoreLink && (
            <li className="py-2 border-primary border-2 border-b-0 first:border-t-0 last:border-b-2 rounded-[2px] leading-tight text-sm bg-white hover:bg-primary hover:text-white transition-all duration-150">
              <Link href={productNumber.usStoreLink} target="_blank" className="text-sm hover:text-white w-full block h-full">
                {t?.all?.order_product_us ?? 'Order from US Store'}
              </Link>
            </li>
          )}
          {productNumber.ausStoreLink && (
            <li className="py-2 border-primary border-2 border-b-0 first:border-t-0 last:border-b-2 rounded-[2px] leading-tight text-sm bg-white hover:bg-primary hover:text-white transition-all duration-150">
              <Link href={productNumber.ausStoreLink} target="_blank" className="text-sm hover:text-white w-full block h-full">
                {t?.all?.order_product_aus ?? 'Order from AUS Store'}
              </Link>
            </li>
          )}
        </ul>
      </div>
    )
  }

  /**
   * Displays the product detail.
   *
   * @param {DrupalProductVariationProductNumber} productNumber - The product number.
   * @param {DrupalProductVariationGroup} variation - The product variation group.
   */
  const productDetailDisplay = (productNumber: DrupalProductVariationProductNumber, variation: DrupalProductVariationGroup) => {
    let label = ``;
    if (productNumber.detail) {
      label = productNumber.detail;
    }
    return label;
  }

  /**
   * Displays the product number.
   *
   * @param {DrupalProductVariationProductNumber} productNumber - The product number.
   */
  const productNumberDisplay = (productNumber: DrupalProductVariationProductNumber): string => {
    if (productNumber.sku && productNumber.sku != productNumber.number) {
      return `${productNumber.number} (${productNumber.sku})`;
    }
    return productNumber.number;
  }

  const productTitle = formatTitle();

  return (
    <ProductSectionDivider id="order-online-section">
      <SectionHeading title={`${t?.all?.buy_product_online_title}`}>
        <FormattedText processed={t?.all?.buy_product_online_desc}/>
      </SectionHeading>
      <div className="overflow-visible">
        <table className="w-full table-auto text-black">
          <thead className="text-left text-black bg-white max-md:hidden">
            <tr>
              <td/>
              <td/>
              <td/>
            </tr>
          </thead>
          <tbody>
            {variationsWithLinks.map((variation, key) => (
              <>
                {variation.productNumbers.map(productNumber => {
                  if (productNumber.usStoreLink || productNumber.ausStoreLink) {
                    return (
                      <tr key={`buy-online--variation-${key}`} className="even:bg-hygienaLight odd:bg-white">
                        <td className="w-[15%] pl-6 py-4">
                          {node?.field_product_image?.[0] && (
                            <MediaImage
                              sizes={"100px"}
                              className="h-[90px] w-[120px] flex"
                              loading="lazy"
                              height={150}
                              width={150}
                              imageStyle={PRODUCT_TEASER_IMAGE_STYLE}
                              style={{objectFit: "contain", objectPosition: "center center"}}
                              media={node.field_product_image[0]}/>
                          )}
                        </td>
                        <td className="py-4">
                          <div className="flex flex-col">
                            <div className="md:text-xl font-bold [&>p]:inline [&>p]:font-bold">
                              <FormattedText processed={productTitle}/>
                            </div>
                            <div className="flex flex-wrap gap-0.5 flex-col">
                              {(productNumber.detail || variation.variation?.type) && <div>{productDetailDisplay(productNumber, variation)}</div>}
                              <div className="text-black opacity-60">{productNumberDisplay(productNumber)}</div>
                            </div>
                          </div>
                        </td>
                        <td className="w-[20%] pr-6 py-4">
                          <VariantButton productNumber={productNumber} />
                        </td>
                      </tr>
                    )
                  }
                  return <></>
                })}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </ProductSectionDivider>
  )
}